import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Lernfälle für die interaktiven Lernhilfen im Chat (super_admin, plattformweit).
 *
 * Ein Fall sagt: in Situation X hilft Komponente Y (`use`) oder nicht
 * (`avoid`), mit der sichtbaren Handlung für den Agenten. Die nächtliche
 * Verdichtung der Nutzung legt Vorschläge an (`source: distilled`,
 * `status: suggested`); erst ein aktiver Fall erreicht die Agents (al-tutor
 * schreibt ihn in den LangGraph Store). Backend: al-tutor
 * `/admin/learning-block-cases`, Migration 218, PRD
 * `features/prd/chat-learning-blocks-agent.md` (Phase 3).
 */

export type LearningBlockCasePolarity = 'use' | 'avoid';
export type LearningBlockCaseStatus = 'suggested' | 'active' | 'retired';
export type LearningBlockCaseSource = 'distilled' | 'manual';

/** Zahlen und Raten aus der Verdichtung — nie Lernerdaten. */
export interface LearningBlockCaseEvidence {
  n?: number;
  /** 0..1 */
  engagement?: number;
  /** 0..1 */
  completion?: number;
  clients?: number;
  thumbs_up?: number;
  thumbs_down?: number;
  /** Aus einem Testlauf ohne Schwellen. */
  forced?: boolean;
}

export interface LearningBlockCase {
  id: string;
  component: string;
  situation: string;
  action: string;
  anchors: string[];
  polarity: LearningBlockCasePolarity;
  status: LearningBlockCaseStatus;
  source: LearningBlockCaseSource;
  evidence: LearningBlockCaseEvidence;
  /** YYYY-MM-DD, null = unbefristet. */
  valid_until: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
  /** Wann die Store-Kopie zuletzt geschrieben wurde; null = steht aus. */
  store_synced_at: string | null;
}

export interface LearningBlockUsage {
  component: string;
  impressions: number;
  failed: number;
  engaged: number;
  completed: number;
  clients: number;
  thumbs_up: number;
  thumbs_down: number;
  shadow_agree: number;
}

export interface LearningBlockCaseRun {
  run_key: string;
  started_at: string;
  finished_at: string | null;
  stats: any | null;
  error: string | null;
}

export interface LearningBlockCaseThresholds {
  min_impressions: number;
  min_clients: number;
  days: number;
}

export interface LearningBlockCasesOverview {
  cases: LearningBlockCase[];
  usage: LearningBlockUsage[];
  /** Erlaubte Komponenten-IDs (Registry). */
  components: string[];
  thresholds: LearningBlockCaseThresholds;
  last_run: LearningBlockCaseRun | null;
}

export interface LearningBlockCaseCreate {
  component: string;
  situation: string;
  action: string;
  anchors: string[];
  polarity: LearningBlockCasePolarity;
  valid_until: string | null;
  status: 'active' | 'suggested';
}

export type LearningBlockCaseUpdate = Partial<
  Pick<LearningBlockCase, 'component' | 'situation' | 'action' | 'anchors' | 'polarity' | 'valid_until'>
>;

export interface LearningBlockCaseDistillResult {
  run_key: string;
  created: number;
  eligible: string[];
  /** Komponente → Grund, warum sie nicht verdichtet wurde. */
  skipped: { [component: string]: string };
  error: string | null;
}

/** Grenzen des Backends — der Dialog prüft dieselben. */
export const LEARNING_BLOCK_CASE_LIMITS = {
  textMax: 300,
  anchorsMax: 8,
  anchorMax: 60,
} as const;

/** Anzeigename einer Komponente (`learningBlockCases.components.<id>`); eine
 *  neue Komponente ohne Übersetzung zeigt ihre ID statt des rohen Schlüssels. */
export function learningBlockComponentLabel(translate: TranslateService, id: string): string {
  const key = `learningBlockCases.components.${id}`;
  const label = translate.instant(key);
  return label && label !== key ? label : id;
}

/** Fehlertext aus einer Backend-Antwort: FastAPI liefert `detail` als Text
 *  oder — bei Validierungsfehlern — als Liste von `{ msg }`. */
export function learningBlockCaseErrorText(err: any): string | null {
  const detail = err?.error?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  if (Array.isArray(detail)) {
    const messages = detail.map((d) => (typeof d === 'string' ? d : d?.msg)).filter((m) => !!m);
    if (messages.length) {
      return messages.join(' · ');
    }
  }
  return null;
}

@Injectable({ providedIn: 'root' })
export class LearningBlockCasesService {
  private readonly base = `${environment.tutorsBackend}/admin/learning-block-cases`;

  constructor(private http: HttpClient) {}

  getOverview(): Observable<LearningBlockCasesOverview> {
    return this.http.get<LearningBlockCasesOverview>(this.base);
  }

  createCase(body: LearningBlockCaseCreate): Observable<LearningBlockCase> {
    return this.http.post<LearningBlockCase>(this.base, body);
  }

  updateCase(id: string, body: LearningBlockCaseUpdate): Observable<LearningBlockCase> {
    return this.http.patch<LearningBlockCase>(`${this.base}/${encodeURIComponent(id)}`, body);
  }

  setStatus(id: string, status: LearningBlockCaseStatus): Observable<LearningBlockCase> {
    return this.http.post<LearningBlockCase>(`${this.base}/${encodeURIComponent(id)}/status`, { status });
  }

  deleteCase(id: string): Observable<{ deleted: boolean }> {
    return this.http.delete<{ deleted: boolean }>(`${this.base}/${encodeURIComponent(id)}`);
  }

  /** `force` ignoriert die Mindestmengen; das Ergebnis bleibt ein Vorschlag. */
  distill(force: boolean): Observable<LearningBlockCaseDistillResult> {
    return this.http.post<LearningBlockCaseDistillResult>(`${this.base}/distill`, { force });
  }
}
